/**
 * The Parallax application shell: login gate, floating glass header, full-bleed
 * map layer, floating fleet rail / command dock, the timeline, and the ⌘K
 * command palette.
 *
 * The shell owns no screen. Feature screens register themselves into the named
 * slots below, which is what keeps this file from becoming the predecessor's
 * App.tsx: 3,400 lines holding the map, the command panel, five modals and the
 * WebSocket handler, where changing the planning UI risked the flight controls.
 *
 * Layout is "Liquid Glass Monochrome": a full-bleed map behind, translucent
 * glass panels floating over it. The chrome toggle (backtick) hides every panel
 * for a clean common operating picture.
 */

import { AlertTriangle, LogOut, Search } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { changePassword } from '@/api/auth';
import { INDICATOR_LABEL } from '@/api/integrity';
import { useFlaggedDrones } from '@/stores/integrityStore';
import { startOutbox, useOutbox } from '@/stores/outbox';
import { onActivity, type ActivityEntry } from '@/api/client';
import { wsClient } from '@/api/ws';
import { useAorScopedCount, useAorScopedIds } from '@/features/fleet/aorScope';
import { MobileTabBar, MobileTopBar } from '@/mobile/MobileChrome';
import { useViewport } from '@/mobile/useViewport';
import { AORS } from '@/map/aors';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ACTIVITY_LOG_LIMIT } from '@/config';
import { cn, formatClock, formatLatency } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import {
  droneActions,
  useAlerts,
  useLinkStatus,
  useDroneStore,
  type LinkStatus,
} from '@/stores/droneStore';
import { useMapStore } from '@/stores/mapStore';
import { useScreen, useWorkspaceStore } from '@/stores/workspaceStore';
import { registeredScreens, useAvailableScreens, useScreenAccess, useScreens } from '@/features/workspace/screens';
import { screenJumps, visibleShortcuts } from '@/features/workspace/shortcuts';

/**
 * Where a feature screen may mount.
 *
 * `mainView` is the map and whatever sits over it. `fleetRail` is the left
 * column of aircraft. `commandBar` is the bottom-center command dock.
 * `topBarActions` is the segmented screen nav in the header. An unfilled slot
 * renders nothing and the layout closes over it.
 */
export type ShellSlot = 'fleetRail' | 'mainView' | 'commandBar' | 'topBarActions';

const slots = new Map<ShellSlot, () => ReactNode>();
const slotListeners = new Set<() => void>();
let slotVersion = 0;

/**
 * Register a screen into the shell. Call at module scope from a feature's
 * `register.tsx`; main.tsx imports those eagerly before the first render.
 */
export function registerShellSlot(slot: ShellSlot, render: () => ReactNode): void {
  slots.set(slot, render);
  slotVersion += 1;
  for (const listener of slotListeners) listener();
}

function subscribeSlots(listener: () => void): () => void {
  slotListeners.add(listener);
  return () => {
    slotListeners.delete(listener);
  };
}

function useSlot(slot: ShellSlot): ReactNode {
  useSyncExternalStore(
    subscribeSlots,
    () => slotVersion,
    () => slotVersion,
  );
  const render = slots.get(slot);
  return render ? render() : null;
}

function Reticle({ className }: { className?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" className={className}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3.2" fill="currentColor" />
      <line x1="12" y1="1" x2="12" y2="6" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

const LINK_LABEL: Record<LinkStatus, string> = {
  idle: 'Standby',
  connecting: 'Connecting',
  open: 'Live',
  reconnecting: 'Reconnecting',
  closed: 'Link lost',
};

/** The LIVE dot + aircraft count on the right of the header. */
function LiveCounts() {
  const status = useLinkStatus();
  const count = useAorScopedCount();
  const live = status === 'open';
  const color = status === 'closed' ? 'var(--danger)' : live ? 'var(--ok)' : 'var(--caution)';

  return (
    <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
      <span
        className={cn('size-[7px] rounded-full', !live && 'px-pulse')}
        style={{ background: color, boxShadow: live ? `0 0 7px ${color}` : undefined }}
      />
      <span
        className="label-mono text-[10px]"
        style={{ color: live ? 'var(--muted-foreground-3)' : color, letterSpacing: '0.12em' }}
      >
        {live ? 'LIVE' : LINK_LABEL[status]}
      </span>
      <span
        data-hide-tight
        className="tnum text-[11px]"
        style={{ color: 'var(--muted-foreground-3)' }}
      >
        {count} AC
      </span>
    </div>
  );
}

function AorDropdown() {
  const aor = useWorkspaceStore((state) => state.aor);
  const setAor = useWorkspaceStore((state) => state.setAor);
  const setScreen = useWorkspaceStore((state) => state.setScreen);
  const flyTo = useMapStore((state) => state.flyTo);
  const [open, setOpen] = useState(false);

  const pick = (id: string) => {
    setScreen('command');
    setAor(id);
    flyTo(id);
    setOpen(false);
  };

  return (
    <span className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Area of responsibility: ${aor}`}
        className="flex items-center gap-2 rounded-[99px] border border-[color:var(--border)] bg-[oklch(1_0_0/0.07)] px-4 py-2 label-mono text-[11px] whitespace-nowrap tablet:px-3"
        style={{ letterSpacing: '0.08em' }}
      >
        <span data-hide-tight>AOR</span> {aor}
        <span style={{ color: 'var(--muted-foreground)' }}>▾</span>
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="glass scroll-thin absolute left-0 top-[calc(100%+10px)] z-20 flex max-h-[calc(100dvh-96px)] min-w-[250px] flex-col gap-0.5 overflow-y-auto rounded-[16px] p-1.5">
            {AORS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => pick(a.id)}
                className="flex items-center gap-2 rounded-[11px] px-[11px] py-2 text-left"
                style={{ background: aor === a.id ? 'oklch(1 0 0 / 0.1)' : 'transparent' }}
              >
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="label-mono text-[10.5px]" style={{ letterSpacing: '0.06em' }}>
                    {a.id}
                  </span>
                  <span className="label-mono text-[9px] text-[color:var(--muted-foreground)]">
                    {a.sub}
                  </span>
                </span>
                {aor === a.id ? <span className="ml-auto text-[10px]">✓</span> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </span>
  );
}

function GlassHeader() {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const chrome = useWorkspaceStore((state) => state.chrome);
  const toggleChrome = useWorkspaceStore((state) => state.toggleChrome);
  const openSearch = useWorkspaceStore((state) => state.openSearch);
  // The screen nav, AOR switcher and search are the command centre's — they
  // appear once you have flown into an AOR, not on the strategic entry globe.
  const entered = useWorkspaceStore((state) => state.entered);
  const nav = useSlot('topBarActions');
  const initials = (user?.username ?? '??').slice(0, 2).toUpperCase();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const compact = useViewport() === 'tablet';

  return (
    <header
      className="glass absolute inset-x-[14px] top-[14px] z-30 flex h-[58px] items-center gap-[14px] rounded-[20px] px-[18px] tablet:gap-[10px] tablet:px-[14px]"
      aria-label="Top bar"
    >
      <div className="flex shrink-0 items-center gap-[10px] text-foreground">
        <Reticle />
        <span data-hide-tight className="text-[14px] font-semibold tracking-[0.18em]">
          parallax
        </span>
      </div>

      {entered ? (
        <>
          <AorDropdown />

          {nav}

          <button
            type="button"
            onClick={toggleChrome}
            title="Hide all panels and see just the COP (`)"
            aria-label={chrome ? 'Focus COP: hide all panels' : 'Show panels'}
            className="shrink-0 rounded-[99px] border border-[color:var(--border-strong)] px-[14px] py-2 label-mono text-[9.5px] whitespace-nowrap tablet:px-3"
            style={{
              letterSpacing: '0.1em',
              background: chrome ? 'transparent' : 'oklch(1 0 0 / 0.12)',
              color: chrome ? 'var(--muted-foreground-3)' : 'var(--foreground)',
            }}
          >
            ▱{compact ? '' : chrome ? ' FOCUS COP' : ' SHOW PANELS'}
          </button>

          <div className="flex min-w-0 flex-1 items-center justify-center">
            {compact ? (
              <button
                type="button"
                onClick={openSearch}
                aria-label="Search everything"
                title="Search everything (⌘K)"
                className="well flex size-9 shrink-0 items-center justify-center rounded-full text-[color:var(--muted-foreground)] hover:text-foreground"
              >
                <Search className="size-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={openSearch}
                className="well flex min-w-0 max-w-[300px] flex-1 items-center gap-2 rounded-[99px] px-[14px] py-2 text-left text-[color:var(--muted-foreground)] wide:max-w-[460px]"
              >
                <Search className="size-3 shrink-0" />
                <span className="truncate text-[12px]">Search everything…</span>
                <span className="ml-auto shrink-0 rounded-[5px] border border-[color:var(--border)] px-[5px] py-px label-mono text-[9.5px]">
                  ⌘K
                </span>
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span
            className="truncate label-mono text-[10px] text-[color:var(--muted-foreground-3)]"
            title="Select an AOR to enter the command centre"
          >
            ENTRY · SELECT AN AOR TO ENTER THE COMMAND CENTRE
          </span>
        </div>
      )}

      <LiveCounts />
      <OutboxPill />

      <div className="flex shrink-0 items-center gap-[10px] whitespace-nowrap border-l border-[color:var(--border)] pl-[14px] tablet:pl-[10px]">
        <button
          type="button"
          title="Change password"
          onClick={() => setPasswordOpen(true)}
          className="inline-flex size-[26px] items-center justify-center rounded-full border border-[color:var(--border-strong)] bg-[oklch(1_0_0/0.12)] label-mono text-[10px] hover:bg-[oklch(1_0_0/0.2)]"
        >
          {initials}
        </button>
        <span data-hide-tight className="max-w-[140px] truncate text-[12.5px]" title={user?.username}>
          {user?.username}
        </span>
        {user ? (
          <span
            data-hide-narrow
            className="rounded-[99px] border border-[color:var(--border)] px-2 py-0.5 label-mono text-[9px] text-[color:var(--muted-foreground)]"
            style={{ letterSpacing: '0.08em' }}
          >
            {user.role.toUpperCase()}
          </span>
        ) : null}
        <button
          type="button"
          aria-label="Sign out"
          title="Sign out"
          onClick={() => void logout()}
          className="hit-44 text-[color:var(--muted-foreground)] hover:text-foreground"
        >
          <LogOut className="size-4" />
        </button>
      </div>

      {passwordOpen ? <ChangePasswordDialog onClose={() => setPasswordOpen(false)} /> : null}
    </header>
  );
}

/**
 * Self-service password change, behind the avatar chip. The server demands the
 * current password, so a walked-away-from session cannot silently take over
 * the account by setting a new one.
 */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const valid = current.length > 0 && next.length >= 12 && next === confirm;

  const submit = () => {
    if (!valid || busy) return;
    setBusy(true);
    void changePassword(current, next)
      .then((result) => {
        if (result.ok) {
          setMessage({ text: 'Password changed. Other sessions are signed out.', ok: true });
          setCurrent('');
          setNext('');
          setConfirm('');
        } else {
          setMessage({ text: result.error.message, ok: false });
        }
      })
      .finally(() => setBusy(false));
  };

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[70] flex items-center justify-center p-6 phone:p-3"
      style={{ background: 'rgba(6,6,8,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Change password"
        className="glass scroll-thin flex max-h-full w-[min(380px,100%)] flex-col gap-3.5 overflow-y-auto rounded-[22px] p-6 phone:p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="label-mono text-[12px]">CHANGE PASSWORD</span>
        <input
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          type="password"
          placeholder="current password"
          autoComplete="current-password"
          className="well h-9 rounded-[99px] px-4 text-[13px] outline-none"
        />
        <input
          value={next}
          onChange={(event) => setNext(event.target.value)}
          type="password"
          placeholder="new password (min 12 characters)"
          autoComplete="new-password"
          className="well h-9 rounded-[99px] px-4 text-[13px] outline-none"
        />
        <input
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          type="password"
          placeholder="repeat new password"
          autoComplete="new-password"
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
          className="well h-9 rounded-[99px] px-4 text-[13px] outline-none"
        />
        {next && confirm && next !== confirm ? (
          <p className="text-[11px]" style={{ color: 'var(--caution)' }}>
            The two new passwords do not match.
          </p>
        ) : null}
        {message ? (
          <p className="text-[11px]" style={{ color: message.ok ? 'var(--ok)' : 'var(--caution)' }}>
            {message.text}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[99px] border border-[color:var(--border-strong)] px-4 py-2 label-mono text-[10px]"
          >
            {message?.ok ? 'DONE' : 'CANCEL'}
          </button>
          <button
            type="button"
            disabled={!valid || busy}
            onClick={submit}
            className="rounded-[99px] bg-white/90 px-4 py-2 label-mono text-[10px] text-[#0c0c0d] disabled:opacity-40"
          >
            {busy ? 'CHANGING…' : 'CHANGE'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Link and queue state, in the header.
 *
 * Silence means connected — a permanent "ONLINE" badge is noise nobody reads,
 * and the point of this is to be noticed on the one day it appears. Offline
 * says so plainly and, if work is waiting, exactly how much, because the worst
 * outcome at the edge is an operator who thinks a route saved when it did not.
 */
function OutboxPill() {
  const { online, entries, flushing } = useOutbox();
  if (online && entries.length === 0) return null;

  const tone = online ? 'var(--ok)' : 'var(--caution)';
  const label = !online
    ? entries.length
      ? `OFFLINE · ${entries.length} QUEUED`
      : 'OFFLINE'
    : flushing
      ? 'SYNCING…'
      : `${entries.length} QUEUED`;

  return (
    <span
      title={
        entries.length
          ? `Waiting for a link: ${entries.map((e) => e.label).join(', ')}`
          : 'No link to the server. Live commands are unavailable; planning still works.'
      }
      className="flex items-center gap-1.5 whitespace-nowrap rounded-[99px] border px-2.5 py-1 label-mono text-[9px]"
      style={{ color: tone, borderColor: `color-mix(in oklch, ${tone} 45%, transparent)` }}
    >
      <span className="size-[6px] rounded-full" style={{ background: tone }} />
      {label}
    </span>
  );
}

/** A degraded-link caution pill, top center. */
function CautionPill() {
  const alerts = useAlerts();
  const flagged = useFlaggedDrones();
  const critical = alerts.filter((alert) => alert.severity === 'critical').slice(0, 1);

  // Integrity outranks an ordinary alert: a jammed or spoofed aircraft is
  // showing an operator a position that may simply be false, and that is worse
  // than any single vehicle fault they might otherwise be reading.
  const suspect = flagged.filter((drone) => drone.severity === 'critical');
  if (suspect.length) {
    const first = suspect[0]!;
    const label = INDICATOR_LABEL[first.findings[0]?.indicator ?? ''] ?? 'INTEGRITY';
    return (
      <div className="pointer-events-none absolute inset-x-0 top-[84px] z-40 flex justify-center">
        <div
          className="glass-pill flex max-w-[min(720px,calc(100vw-32px))] items-center gap-2 rounded-[99px] px-4 py-2 text-[12px]"
          style={{ borderColor: 'color-mix(in oklch, var(--danger) 70%, transparent)' }}
        >
          <AlertTriangle className="size-4 shrink-0 text-[color:var(--danger-foreground)]" />
          <span className="label-mono text-[9px] text-[color:var(--danger-foreground)]">
            {label}
          </span>
          <span className="truncate">
            {first.droneId}
            {suspect.length > 1 ? ` +${suspect.length - 1} more` : ''} — {first.findings[0]?.detail}
          </span>
        </div>
      </div>
    );
  }

  if (!critical.length) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 top-[84px] z-40 flex justify-center">
      <div
        className="glass-pill flex items-center gap-2 rounded-[99px] px-4 py-2 text-[12px]"
        style={{ borderColor: 'color-mix(in oklch, var(--danger) 55%, transparent)' }}
      >
        <AlertTriangle className="size-4 shrink-0 text-[color:var(--danger-foreground)]" />
        <span className="truncate">{critical[0]?.message}</span>
      </div>
    </div>
  );
}

/**
 * The ⌘K command palette. Navigates screens and jumps to aircraft (selecting
 * them on the map). Wired to the live fleet; the full cross-entity index
 * (plans, flights, AORs, actions) lands as those data sources are wired.
 */
function CommandPalette() {
  const open = useWorkspaceStore((state) => state.searchOpen);
  const close = useWorkspaceStore((state) => state.closeSearch);
  const setScreen = useWorkspaceStore((state) => state.setScreen);
  const ids = useAorScopedIds();
  const available = useAvailableScreens();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setIndex(0);
      const id = window.setTimeout(() => inputRef.current?.focus(), 20);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const screens: Array<{ id: string; label: string; run: () => void }> = available
      .filter((s) => !q || s.label.toLowerCase().includes(q))
      .map((s) => ({
        id: `screen:${s.id}`,
        label: s.label,
        run: () => setScreen(s.id),
      }));

    const aircraft = ids
      .map((id) => ({ id, callsign: droneActions.getDrone(id)?.callsign ?? id }))
      .filter(({ callsign, id }) => {
        if (!q) return true;
        return callsign.toLowerCase().includes(q) || id.toLowerCase().includes(q);
      })
      .slice(0, 8)
      .map(({ id, callsign }) => ({
        id: `ac:${id}`,
        label: callsign,
        run: () => {
          setScreen('command');
          useDroneStore.getState().select(id);
        },
      }));

    const groups: Array<{ name: string; items: typeof screens }> = [];
    if (screens.length) groups.push({ name: 'SCREENS', items: screens });
    if (aircraft.length) groups.push({ name: 'AIRCRAFT', items: aircraft });
    return groups;
  }, [query, ids, available, setScreen]);

  const flat = useMemo(() => results.flatMap((g) => g.items), [results]);
  const active = flat.length ? ((index % flat.length) + flat.length) % flat.length : 0;

  if (!open) return null;

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIndex((i) => i + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIndex((i) => i - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      flat[active]?.run();
      close();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  };

  return (
    <div
      className="absolute inset-0 z-[60] flex items-start justify-center px-6 pt-[88px] pb-6 phone:px-3 phone:pt-[calc(var(--safe-t)+68px)] phone:pb-3"
      style={{ background: 'rgba(6,6,8,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={close}
    >
      <div
        className="glass flex max-h-full w-[min(560px,100%)] flex-col overflow-hidden rounded-[20px]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-[color:var(--border-nested)] px-[18px] py-[15px]">
          <Search className="size-4 shrink-0 text-[color:var(--muted-foreground-3)]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setIndex(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search aircraft, screens, actions…"
            className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-[color:var(--muted-foreground)]"
          />
          <span className="shrink-0 rounded-[5px] border border-[color:var(--border)] px-[6px] py-0.5 label-mono text-[9px] text-[color:var(--muted-foreground)]">
            ESC
          </span>
        </div>

        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-2">
          {flat.length === 0 ? (
            <div className="px-[10px] py-[26px] text-center text-[12px] text-[color:var(--muted-foreground)]">
              No matches for “{query}”. Try a callsign or a screen name.
            </div>
          ) : (
            results.map((group) => (
              <div key={group.name} className="mb-1.5">
                <div className="label-mono px-[10px] pt-2 pb-[5px] text-[8.5px] text-[color:var(--muted-foreground-2)]">
                  {group.name}
                </div>
                {group.items.map((item) => {
                  const isActive = flat[active]?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        item.run();
                        close();
                      }}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-[12px] px-[10px] py-[9px] text-left',
                        isActive && 'bg-[oklch(1_0_0/0.08)]',
                      )}
                    >
                      <span className="tnum text-[12.5px]">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-[14px] border-t border-[color:var(--border-nested)] px-4 py-[9px] label-mono text-[9.5px] text-[color:var(--muted-foreground-2)]">
          <span>{flat.length} results</span>
          <span data-hide-narrow className="ml-auto">
            ↵ open · ESC close
          </span>
        </div>
      </div>
    </div>
  );
}

function LoginScreen() {
  const login = useAuthStore((state) => state.login);
  const busy = useAuthStore((state) => state.busy);
  const error = useAuthStore((state) => state.error);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!username || !password) return;
    void login(username, password);
  };

  return (
    <div
      className="flex h-full items-center justify-center px-4"
      style={{
        background: 'radial-gradient(1200px 800px at 50% -10%, oklch(0.2 0 0), var(--map-base))',
      }}
    >
      <form onSubmit={submit} className="glass w-full max-w-sm rounded-[20px] p-7">
        <div className="flex items-center gap-[10px] text-foreground">
          <Reticle />
          <span className="text-[15px] font-semibold tracking-[0.18em]">parallax</span>
        </div>
        <p className="mt-3 text-[13px] text-[color:var(--muted-foreground-3)]">
          Multi-domain command &amp; control. Sessions are per operator; every command is logged.
        </p>

        <div className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="username">Operator</Label>
            <Input
              id="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
        </div>

        {error ? (
          <p className="mt-4 flex items-start gap-2 text-[13px] text-[color:var(--danger-foreground)]">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded-[99px] bg-[oklch(1_0_0/0.9)] py-2.5 text-[13px] font-semibold text-[#0c0c0d] disabled:opacity-60"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="mt-4 label-mono text-[9px] text-[color:var(--muted-foreground-2)]">
          NO SELF-REGISTRATION · EVERY COMMAND LOGGED
        </p>
      </form>
    </div>
  );
}

/**
 * The activity ledger, docked as the bottom timeline. Fed by the API client's
 * timing callback: every call lands here whether it succeeded or not. In the
 * predecessor a write failure was silent, so the only sign a mission upload had
 * been rejected was that the aircraft did not fly it.
 */
const TIMELINE_OPEN_PX = 168;
const TIMELINE_CLOSED_PX = 40;
/** Timeline height plus its 14px inset and a 12px gap above it. */
const timelineReserve = (open: boolean) =>
  `${(open ? TIMELINE_OPEN_PX : TIMELINE_CLOSED_PX) + 26}px`;

function Timeline() {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const open = useWorkspaceStore((state) => state.timelineOpen);
  const setOpen = useWorkspaceStore((state) => state.setTimelineOpen);

  useEffect(
    () =>
      onActivity((entry) => {
        setEntries((previous) => [entry, ...previous].slice(0, ACTIVITY_LOG_LIMIT));
      }),
    [],
  );

  const failureCount = useMemo(() => entries.filter((entry) => !entry.ok).length, [entries]);

  return (
    <section
      // data-map-panel: this covers the bottom strip of the map, so the camera
      // has to account for it when an aircraft is selected. See panelAvoidance.
      data-map-panel="timeline"
      className="glass absolute inset-x-[14px] bottom-[14px] z-20 flex flex-col overflow-hidden rounded-[18px]"
      style={{ height: open ? TIMELINE_OPEN_PX : TIMELINE_CLOSED_PX }}
      aria-label="Timeline and activity"
    >
      <header className="flex h-10 shrink-0 items-center gap-3 px-4">
        <span className="label-mono text-[9px] text-[color:var(--muted-foreground-3)]">TIMELINE</span>
        <span className="flex items-center gap-1.5">
          <span className="size-[6px] rounded-full" style={{ background: 'var(--ok)' }} />
          <span className="label-mono text-[8.5px]" style={{ color: 'var(--ok)' }}>
            LIVE
          </span>
        </span>
        <span className="ml-auto flex items-center gap-3">
          <span className="label-mono text-[9px] text-[color:var(--muted-foreground-3)]">
            ACTIVITY
          </span>
          {failureCount > 0 ? (
            <span
              className="rounded-[99px] px-2 py-0.5 label-mono text-[8.5px]"
              style={{
                color: 'var(--danger-foreground)',
                background: 'color-mix(in oklch, var(--danger) 18%, transparent)',
                borderColor: 'color-mix(in oklch, var(--danger) 45%, transparent)',
              }}
            >
              {failureCount} FAILED
            </span>
          ) : null}
          <button
            type="button"
            className="label-mono text-[8.5px] text-[color:var(--muted-foreground-3)]"
            onClick={() => setOpen(!open)}
          >
            {open ? 'COLLAPSE' : 'EXPAND'}
          </button>
        </span>
      </header>

      {open ? (
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto border-t border-[color:var(--border-nested)]">
          {entries.length === 0 ? (
            <p className="px-4 py-2 text-[11px] text-[color:var(--muted-foreground)]">
              No calls yet this session.
            </p>
          ) : (
            <ul>
              {entries.map((entry) => (
                <li
                  key={entry.id}
                  className={cn(
                    'flex items-baseline gap-3 px-4 py-1 text-[11px]',
                    !entry.ok && 'bg-[color-mix(in_oklch,var(--danger)_12%,transparent)]',
                  )}
                >
                  <span className="tnum shrink-0 text-[color:var(--muted-foreground-3)]">
                    {formatClock(entry.at)}
                  </span>
                  <span className="tnum w-12 shrink-0 text-[color:var(--muted-foreground-3)]">
                    {entry.method}
                  </span>
                  <span
                    className="tnum w-10 shrink-0"
                    style={{ color: entry.ok ? 'var(--ok)' : 'var(--danger-foreground)' }}
                  >
                    {entry.status || '---'}
                  </span>
                  <span className="min-w-0 truncate" title={entry.path}>
                    {entry.label ?? entry.path}
                  </span>
                  {entry.message ? (
                    <span
                      className="min-w-0 truncate"
                      style={{ color: 'var(--danger-foreground)' }}
                      title={entry.message}
                    >
                      {entry.message}
                    </span>
                  ) : null}
                  <span className="tnum ml-auto shrink-0 text-[color:var(--muted-foreground-3)]">
                    {formatLatency(entry.latencyMs)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

function Shell() {
  const chrome = useWorkspaceStore((state) => state.chrome);
  const screen = useScreen();
  const entered = useWorkspaceStore((state) => state.entered);
  const timelineOpen = useWorkspaceStore((state) => state.timelineOpen);
  const railDrawer = useWorkspaceStore((state) => state.railDrawer);
  const tier = useViewport();
  const isMobile = tier === 'phone';
  // The fleet rail and timeline belong to the tactical COP — not the strategic
  // globe. Show them only on Command, once flown into an AOR.
  const tacticalCommand = screen === 'command' && entered;
  const fleetRail = useSlot('fleetRail');
  const mainView = useSlot('mainView');
  const [passwordOpen, setPasswordOpen] = useState(false);

  // A tablet has under 500px of map between the header and an open timeline,
  // so it starts collapsed there. Only on entering the tier, so an operator
  // who opens it again keeps it open.
  const previousTier = useRef<typeof tier | null>(null);
  useEffect(() => {
    const ws = useWorkspaceStore.getState();
    const was = previousTier.current;
    previousTier.current = tier;
    if (tier === 'tablet' && was !== 'tablet') ws.setTimelineOpen(false);
    if (was === 'tablet' && tier !== 'tablet') {
      ws.setTimelineOpen(true);
      ws.setRailDrawer(null);
    }
  }, [tier]);

  if (isMobile) {
    return (
      <div
        className="relative h-full w-full overflow-hidden"
        style={{ background: 'var(--map-base)', '--mobile-nav-h': '60px' } as CSSProperties}
      >
        <main className="absolute inset-0">{mainView}</main>
        {chrome ? (
          <>
            <MobileTopBar onChangePassword={() => setPasswordOpen(true)} />
            <CautionPill />
            <MobileTabBar />
          </>
        ) : (
          <button
            type="button"
            onClick={() => useWorkspaceStore.getState().setChrome(true)}
            className="glass-pill absolute bottom-[14px] right-[14px] z-40 rounded-[99px] px-[14px] py-2 label-mono text-[9.5px]"
            style={{ letterSpacing: '0.1em' }}
          >
            ▱ SHOW PANELS
          </button>
        )}
        <CommandPalette />
        <ShortcutHelp />
        {passwordOpen ? <ChangePasswordDialog onClose={() => setPasswordOpen(false)} /> : null}
      </div>
    );
  }

  // Tablet: the fleet rail is a drawer the Command overlay opens, one rail at
  // a time, so the dock keeps the middle of the screen.
  const showFleetRail = tacticalCommand && fleetRail && (tier !== 'tablet' || railDrawer === 'fleet');

  return (
    <div
      className="relative h-full w-full overflow-hidden"
      data-cop={tacticalCommand ? 'tactical' : undefined}
      style={
        {
          background: 'var(--map-base)',
          '--tl-reserve': tacticalCommand ? timelineReserve(timelineOpen) : '14px',
        } as CSSProperties
      }
    >
      {/* Full-bleed screen / map layer. Each screen owns its own layout; the
          shell adds only the header and — on Command — the fleet rail and
          timeline, so switching to another screen never leaves them behind. */}
      <main className="absolute inset-0">{mainView}</main>

      {chrome ? (
        <>
          <GlassHeader />
          <CautionPill />

          {showFleetRail ? (
            <aside
              data-map-panel="fleet-rail"
              className="glass absolute left-[14px] top-[86px] bottom-[var(--tl-reserve)] z-20 flex w-[var(--rail-l-w)] flex-col overflow-hidden rounded-[20px] tablet:top-[136px] tablet:z-30 tablet:max-w-[calc(100vw-28px)]"
            >
              {fleetRail}
            </aside>
          ) : null}

          {tacticalCommand ? <Timeline /> : null}
        </>
      ) : (
        <button
          type="button"
          onClick={() => useWorkspaceStore.getState().setChrome(true)}
          className="glass-pill absolute bottom-[14px] right-[14px] z-30 rounded-[99px] px-[14px] py-2 label-mono text-[9.5px]"
          style={{ letterSpacing: '0.1em' }}
        >
          ▱ SHOW PANELS
        </button>
      )}

      <CommandPalette />
      <ShortcutHelp />
    </div>
  );
}

/** The cheat sheet for the shell's keys: ⌘K search, backtick chrome toggle, number-key screen jumps. */
function ShortcutHelp() {
  const open = useWorkspaceStore((state) => state.helpOpen);
  const close = useWorkspaceStore((state) => state.closeHelp);
  const screens = useScreens();
  const access = useScreenAccess();
  if (!open) return null;
  const rows = visibleShortcuts(screens, access);

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 phone:p-3"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        className="glass scroll-thin max-h-full w-[min(420px,100%)] overflow-y-auto rounded-[18px] p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="label-mono text-[9px]" style={{ letterSpacing: '0.14em', color: 'rgba(244,244,245,0.55)' }}>
            KEYBOARD SHORTCUTS
          </span>
          <button
            type="button"
            onClick={close}
            className="ml-auto label-mono text-[9px] text-[color:var(--muted-foreground-2)] hover:text-white"
          >
            ESC
          </button>
        </div>
        <div className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <div key={row.keys} className="flex items-baseline gap-3 text-[12px]">
              <kbd
                className="tnum rounded-[6px] px-2 py-0.5 font-mono text-[11px]"
                style={{ background: 'rgba(255,255,255,0.08)', minWidth: 72, textAlign: 'center' }}
              >
                {row.keys}
              </kbd>
              <span style={{ color: '#f4f4f5' }}>{row.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function useShellKeybindings(): void {
  const { isAdmin, canPlan } = useScreenAccess();

  useEffect(() => {
    const isTyping = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    const onKey = (event: KeyboardEvent) => {
      const ws = useWorkspaceStore.getState();
      // No command-centre shortcuts on the strategic entry globe.
      if (!ws.entered) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        ws.toggleSearch();
        return;
      }
      if (ws.searchOpen) return;
      if (isTyping(event.target)) return;

      if (event.key === '`') {
        event.preventDefault();
        ws.toggleChrome();
        return;
      }
      if (event.key === '?') {
        event.preventDefault();
        ws.toggleHelp();
        return;
      }
      if (event.key === 'Escape' && ws.helpOpen) {
        event.preventDefault();
        ws.closeHelp();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = screenJumps(registeredScreens(), { canPlan, isAdmin }).find(
        ({ key }) => key === event.key,
      );
      if (target) {
        event.preventDefault();
        ws.setScreen(target.screen.id);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isAdmin, canPlan]);
}

function AuthenticatedApp() {
  useShellKeybindings();
  return <Shell />;
}

export function App() {
  const status = useAuthStore((state) => state.status);
  const bootstrap = useAuthStore((state) => state.bootstrap);
  const tier = useViewport();

  // The screens and styles.css read these to reflow; set here rather than in
  // the shell so the login form gets the phone rules (16px fields) as well.
  useLayoutEffect(() => {
    const body = document.body;
    body.dataset.viewport = tier;
    if (tier === 'phone') body.dataset.mobile = '1';
    else delete body.dataset.mobile;
    return () => {
      delete body.dataset.viewport;
      delete body.dataset.mobile;
    };
  }, [tier]);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // The socket follows the session. Opening it before there is a token would
  // produce a connection the server is required to feed nothing.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    wsClient.connect();
    return () => wsClient.disconnect();
  }, [status]);

  // Replay anything planned while the link was down. Tied to the session
  // because every queued request needs a token to land.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    return startOutbox();
  }, [status]);

  const content = useCallback(() => {
    if (status === 'unknown') {
      return <div className="h-full" style={{ background: 'var(--map-base)' }} aria-busy="true" />;
    }
    return status === 'authenticated' ? <AuthenticatedApp /> : <LoginScreen />;
  }, [status]);

  return <TooltipProvider delayDuration={300}>{content()}</TooltipProvider>;
}

export default App;
